// js/AdminBoard/CustomerSupportBoard/DeviceDetail/DeviceDetail.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import deviceProfile from "images/device.png";
import CheckIcon from "@mui/icons-material/CheckRounded";
import XIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import ContentCopy from "@mui/icons-material/ContentCopy";

import {
    fetchDevice,
    fetchDeviceImage,
    fetchLinkedDevices as fetchLinkedDevicesApi,
    updateDeviceName,
    updateDeviceAvatarAndAccountName,
    updateKonnectID,
    updateDeviceAccountId,
} from "js/Api/index";

import ContactsTab from "./Tabs/ContactsTab";
import SipInfoTab from "./Tabs/SipInfoTab";
import DeviceSettingsTab from "./Tabs/DeviceSettingsTab";
import DeviceFeaturesTab from "./Tabs/DeviceFeaturesTab";
import CallLogsTab from "./Tabs/CallLogsTab";

function DeviceDetail({ deviceId, device: initialDevice, onBack }) {
    const [detailDevice, setDetailDevice] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [deviceImage, setDeviceImage] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);

    const [selectedTab, setSelectedTab] = useState("Contacts");
    const [linkedDevices, setLinkedDevices] = useState([]);

    const [isEditingName, setIsEditingName] = useState(false);
    const [newName, setNewName] = useState("");

    const [isEditingKonnektId, setIsEditingKonnektId] = useState(false);
    const [newKonnektId, setNewKonnektId] = useState("");
    const [showRenameKonnektIdModal, setShowRenameKonnektIdModal] =
        useState(false);

    const [isEditingAccountId, setIsEditingAccountId] = useState(false);
    const [newAccountId, setNewAccountId] = useState("");
    const [showUpdateAccountIdModal, setShowUpdateAccountIdModal] =
        useState(false);

    const [showRenameModal, setShowRenameModal] = useState(false);

    const fileInputRef = useRef(null);
    const [showUpdateAvatarModal, setShowUpdateAvatarModal] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);

    const device = useMemo(() => {
        return {
            ...(initialDevice || {}),
            ...(detailDevice || {}),
        };
    }, [initialDevice, detailDevice]);

    const [iseditDeviceInfo, setiseditDeviceInfo] = useState(false);
    const [editDevice, seteditDevice] = useState(null);
    const [editrecoveryEmail, seteditrecoveryEmail] = useState(editDevice?.recoveryEmail);
    const [editisManager, seteditisManager] = useState(editDevice?.isLeader);
    const [editShortName, seteditShortName] = useState(editDevice?.shortName);
    const [editKonnektskypeid, seteditKonnektskypeid] = useState(editDevice?.konnektDeviceId);
    const [editPrefferedlanguage, seteditPrefferedlanguage] = useState(editDevice?.preferredLang || "en");

    const openEditDevice = (d) => {
        seteditDevice(d);
        seteditrecoveryEmail(d?.recoveryEmail);
        seteditisManager(d?.isLeader);
        seteditShortName(d?.shortName);
        seteditKonnektskypeid(d?.konnektDeviceId);
        seteditPrefferedlanguage(d?.preferredLang || "en");
        setiseditDeviceInfo(true)
    }

    const isKonnektDevice = !!device.konnektDeviceId;
    const isCareWindowDevice = device?.deviceType === "android-carewindow";

    const loadDetail = async (id) => {
        if (!id) return;
        setLoadingDetail(true);
        try {
            const result = await fetchDevice(id);
            if (!result.success) {
                throw new Error(result.error || "Failed to fetch device");
            }
            setDetailDevice(result.data || {});
        } catch (err) {
            console.error("Error loading device detail:", err);
            toast.error("Failed to load device detail");
        } finally {
            setLoadingDetail(false);
        }
    };

    const loadDeviceImage = async (id) => {
        if (!id) return;
        setLoadingImage(true);
        try {
            const result = await fetchDeviceImage(id);
            if (result.success && result.data?.profilePicture) {
                setDeviceImage(result.data.profilePicture);
            } else {
                setDeviceImage(null);
            }
        } catch (err) {
            console.error("Error loading device image:", err);
            setDeviceImage(null);
        } finally {
            setLoadingImage(false);
        }
    };

    const loadLinkedDevices = async (id) => {
        if (!id) return;
        try {
            const result = await fetchLinkedDevicesApi(id);
            if (result.success) {
                const filtered = (result.data || []).filter((d) => d.id !== id);
                setLinkedDevices(filtered);
            } else {
                console.log("Failed to fetch linked devices:", result.error);
            }
        } catch (err) {
            console.error("Error fetching linked devices:", err);
        }
    };

    const refreshDevice = async (options = {}) => {
        const { reloadImage = false, reloadLinkedDevices = true } = options;

        if (!deviceId) return;

        await loadDetail(deviceId);

        if (reloadImage) {
            await loadDeviceImage(deviceId);
        }

        if (reloadLinkedDevices) {
            await loadLinkedDevices(deviceId);
        }
    };

    useEffect(() => {
        if (!deviceId) {
            setDetailDevice(null);
            setDeviceImage(null);
            setLinkedDevices([]);
            setNewName("");
            setNewKonnektId("");
            setNewAccountId("");
            setIsEditingName(false);
            setIsEditingKonnektId(false);
            setIsEditingAccountId(false);
            return;
        }

        const name =
            (initialDevice && initialDevice.name) ||
            (detailDevice && detailDevice.name) ||
            "";
        const konnekt =
            (initialDevice && initialDevice.konnektDeviceId) ||
            (detailDevice && detailDevice.konnektDeviceId) ||
            "";
        const accountId =
            (initialDevice && initialDevice.accountId) ||
            (detailDevice && detailDevice.accountId) ||
            "";

        setNewName(name);
        setNewKonnektId(konnekt);
        setNewAccountId(accountId);
        setIsEditingName(false);
        setIsEditingKonnektId(false);
        setIsEditingAccountId(false);

        refreshDevice({ reloadImage: true });
    }, [deviceId]);

    if (!deviceId) {
        return (
            <div>
                <p style={{ marginTop: 20 }}>No device id found.</p>
            </div>
        );
    }

    const hasAnyDeviceData =
        device && Object.keys(device).some((k) => device[k] !== undefined);

    if (!hasAnyDeviceData && loadingDetail) {
        return (
            <div className="text-center" style={{ padding: "50px" }}>
                <i
                    className="fa fa-spinner fa-spin"
                    style={{ fontSize: "48px", color: "#007bff" }}
                />
                <p style={{ marginTop: "20px", fontSize: "18px" }}>
                    Loading device detail...
                </p>
            </div>
        );
    }

    const handleNameEdit = () => setIsEditingName(true);

    const handleCancelEditName = () => {
        const name =
            device.name ||
            (initialDevice && initialDevice.name) ||
            (detailDevice && detailDevice.name) ||
            "";
        setNewName(name);
        setIsEditingName(false);
    };

    const handleNameSubmitClick = () => setShowRenameModal(true);

    const handleNameSubmit = async () => {
        const result = await updateDeviceName(device.id, newName);
        if (result.success) {
            toast.success(result.message || "Device name updated");
            setIsEditingName(false);
            await refreshDevice();
        } else {
            toast.error(result.error || "Failed to update name");
        }
    };

    const handleRenameConfirm = async () => {
        await handleNameSubmit();
        setShowRenameModal(false);
    };

    const handleEditKonnektId = () => setIsEditingKonnektId(true);

    const handleCancelEditKonnektId = () => {
        const konnekt =
            device.konnektDeviceId ||
            (initialDevice && initialDevice.konnektDeviceId) ||
            (detailDevice && detailDevice.konnektDeviceId) ||
            "";
        setNewKonnektId(konnekt);
        setIsEditingKonnektId(false);
    };

    const handleKonnektIdSubmitClick = () => setShowRenameKonnektIdModal(true);

    const handleKonnektIDSubmit = async () => {
        const result = await updateKonnectID(device.id, newKonnektId);
        if (result.success) {
            toast.success(result.message || "Konnekt Id updated");
            setIsEditingKonnektId(false);
            await refreshDevice();
        } else {
            toast.error(result.error || "Failed to update Konnekt Id");
        }
    };

    const handleRenameKonnektIdConfirm = async () => {
        await handleKonnektIDSubmit();
        setShowRenameKonnektIdModal(false);
    };

    const handleEditAccountId = () => setIsEditingAccountId(true);

    const handleCancelEditAccountId = () => {
        const accountId =
            device.accountId ||
            (initialDevice && initialDevice.accountId) ||
            (detailDevice && detailDevice.accountId) ||
            "";
        setNewAccountId(accountId);
        setIsEditingAccountId(false);
    };

    const handleAccountIdSubmitClick = () => {
        const isValidGuid =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                newAccountId
            );
        if (!isValidGuid) {
            toast.error("Invalid Account ID format. Must be a valid GUID.");
            return;
        }
        setShowUpdateAccountIdModal(true);
    };

    const handleAccountIdSubmit = async () => {
        try {
            const result = await updateDeviceAccountId(device.id, newAccountId);
            if (result.success) {
                toast.success(result.message || "Account ID updated successfully");
                setIsEditingAccountId(false);
                await refreshDevice();
            } else {
                toast.error(result.error || "Failed to update Account ID");
            }
        } catch (error) {
            console.error("Error updating Account ID:", error);
            toast.error(error.message || "Failed to update Account ID");
        }
    };

    const handleUpdateAccountIdConfirm = async () => {
        await handleAccountIdSubmit();
        setShowUpdateAccountIdModal(false);
    };

    const handlePhotoEdit = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImageFile(file);
            setShowUpdateAvatarModal(true);
        }
    };

    const handleCancelAvatar = () => {
        setShowUpdateAvatarModal(false);
        setSelectedImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleConfirmAvatarUpdate = async () => {
        if (selectedImageFile) {
            const result = await updateDeviceAvatarAndAccountName(
                device.id,
                selectedImageFile
            );
            if (result.success) {
                toast.success(result.message || "Profile picture updated");
                await refreshDevice({ reloadImage: true });
            } else {
                toast.error(result.error || "Failed to update profile picture");
            }
        }
        setShowUpdateAvatarModal(false);
        setSelectedImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const copyToClipboard = (value) => {
        if (value === null || value === undefined || value === "") return;
        navigator.clipboard.writeText(String(value));
        toast.success("Copied");
    };

    const firstNonEmpty = (...vals) => {
        for (const v of vals) {
            if (
                v === 0 ||
                v === false ||
                (v !== null && v !== undefined && String(v).trim() !== "")
            ) {
                return v;
            }
        }
        return undefined;
    };

    const formatDate = (value) => {
        if (!value) return "";
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
    };

    const parseTimeZone = (raw) => {
        if (!raw) return "";

        let obj = raw;

        if (typeof raw === "string") {
            try {
                obj = JSON.parse(raw);
            } catch {
                return raw;
            }
        }

        if (typeof obj === "object" && obj.timezone) {
            const tz = obj.timezone;
            const offsetMinutes =
                typeof obj.offset === "number" ? obj.offset : obj.offsetMinutes;

            if (typeof offsetMinutes === "number") {
                const sign = offsetMinutes >= 0 ? "+" : "-";
                const abs = Math.abs(offsetMinutes);
                const hours = String(Math.floor(abs / 60)).padStart(2, "0");
                const minutes = String(abs % 60).padStart(2, "0");
                return `${tz} (UTC${sign}${hours}:${minutes})`;
            }

            return tz;
        }

        return String(raw);
    };

    const InfoRow = ({ label, value }) => {
        const hasValue =
            value === 0 ||
            value === false ||
            (value !== null &&
                value !== undefined &&
                String(value).trim() !== "");

        const display = hasValue ? String(value) : "N/A";

        return (
            <div style={{ marginBottom: 4, display: "flex", alignItems: "center" }}>
                <strong style={{ marginRight: 4 }}>{label}</strong>
                <span>{display}</span>
                {hasValue && (
                    <ContentCopy
                        sx={{ marginLeft: "6px", cursor: "pointer", fontSize: "1rem" }}
                        onClick={() => copyToClipboard(value)}
                    />
                )}
            </div>
        );
    };

    const accountIdDisplay = firstNonEmpty(
        device.accountId,
        device.accountID,
        device.account_id
    );

    const orgDisplay = firstNonEmpty(
        device.organizationName,
        device.orgName,
        device.orgCode
    );

    const serialDisplay = firstNonEmpty(
        device.serialNo,
        device.serialNumber,
        device.serial
    );

    const deviceTypeDisplay = firstNonEmpty(
        device.deviceType,
        device.type,
        device.device_type
    );

    const linkedDevicesDisplay =
        device.linkedDevicesCount !== undefined &&
            device.linkedDevicesCount !== null
            ? device.linkedDevicesCount - 1
            : "";

    const appVersionDisplay = firstNonEmpty(
        device.apP_Version,
        device.appVersion,
        device.app_Version
    );

    const preferredLangDisplay = firstNonEmpty(
        device.preferredLang,
        device.language,
        device.lang
    );

    const createdDisplay = device.createdAt ? formatDate(device.createdAt) : "";

    const updatedDisplay = device.updatedAt ? formatDate(device.updatedAt) : "";

    const timeZoneDisplay = parseTimeZone(
        firstNonEmpty(device.timeZone, device.timezone)
    );

    const sipPhoneDisplay =
        Array.isArray(device.sipPhoneNumbers) && device.sipPhoneNumbers.length > 0
            ? device.sipPhoneNumbers[0]
            : "";

    const handleUpdateDevice = async () => {
        const result = await updateDeviceName(editDevice.id, editDevice.name, editrecoveryEmail, editPrefferedlanguage, editShortName, editKonnektskypeid, editisManager);
        if (result.success) {
            toast.success("Device updated");
            setiseditDeviceInfo(false);
            await refreshDevice();
        } else {
            toast.error(result.error || "Failed to update device");
        }
    }

    return (
        <div>
            <div
                className="white_shd full margin_bottom_30"
                style={{ paddingLeft: "10px", paddingRight: "10px" }}
            >
                <div
                    style={{
                        display: "flex",
                        marginTop: "40px",
                        marginLeft: "40px",
                    }}
                >
                    <div style={{ position: "relative" }}>
                        {loadingImage ? (
                            <div
                                className="imgUserProfilePic"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f0f0f0",
                                }}
                            >
                                <i
                                    className="fa fa-spinner fa-spin"
                                    style={{ fontSize: "24px", color: "#007bff" }}
                                />
                            </div>
                        ) : deviceImage ? (
                            <img
                                className="imgUserProfilePic"
                                src={`data:image/png;base64,${deviceImage}`}
                                alt={device.name}
                            />
                        ) : (
                            <img
                                className="imgUserProfilePic"
                                src={deviceProfile}
                                alt="Default profile"
                            />
                        )}
                        <div
                            onClick={handlePhotoEdit}
                            style={{
                                position: "absolute",
                                right: "8px",
                                top: "8px",
                                cursor: "pointer",
                                background: "#fff",
                                borderRadius: "50%",
                                padding: "4px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                zIndex: 2,
                            }}
                        >
                            <EditIcon sx={{ fontSize: "1.2rem", color: "black" }} />
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                            accept="image/*"
                        />
                    </div>

                    <div
                        style={{
                            marginLeft: "12px",
                            marginTop: "60px",
                            marginBottom: "30px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {isEditingName ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        style={{
                                            border: "none",
                                            borderBottom: "1px solid #ddd",
                                            fontSize: "24px",
                                            padding: "0 4px",
                                            outline: "none",
                                        }}
                                    />
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <div
                                            onClick={handleNameSubmitClick}
                                            style={{ cursor: "pointer", color: "#4CAF50" }}
                                        >
                                            <CheckIcon sx={{ fontSize: "1.2rem" }} />
                                        </div>
                                        <div
                                            onClick={handleCancelEditName}
                                            style={{ cursor: "pointer", color: "#FF5252" }}
                                        >
                                            <XIcon sx={{ fontSize: "1.2rem" }} />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h2 style={{ margin: 0 }}>
                                        {device.name || "Unnamed device"}
                                    </h2>
                                    <div
                                        onClick={handleNameEdit}
                                        style={{
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            color: "#000",
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: "1.2rem" }} />
                                    </div>
                                </>
                            )}
                        </div>

                        <h4 style={{ margin: 0 }}>
                            Device Type: {deviceTypeDisplay || "N/A"}

                        </h4>
                        <h4 style={{ margin: 0 }}>
                            Family Role : {device.isLeader ? "Manager" : "Normal user"}
                        </h4>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "14px", color: "#666" }}>
                                Account ID:
                            </span>
                            {isEditingAccountId ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={newAccountId}
                                        onChange={(e) => setNewAccountId(e.target.value)}
                                        style={{
                                            border: "none",
                                            borderBottom: "1px solid #ddd",
                                            fontSize: "14px",
                                            padding: "0 4px",
                                            outline: "none",
                                            fontFamily: "monospace",
                                            width: "280px",
                                        }}
                                        placeholder="00000000-0000-0000-0000-000000000000"
                                    />
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <div
                                            onClick={handleAccountIdSubmitClick}
                                            style={{ cursor: "pointer", color: "#4CAF50" }}
                                        >
                                            <CheckIcon sx={{ fontSize: "1.2rem" }} />
                                        </div>
                                        <div
                                            onClick={handleCancelEditAccountId}
                                            style={{ cursor: "pointer", color: "#FF5252" }}
                                        >
                                            <XIcon sx={{ fontSize: "1.2rem" }} />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <span
                                        style={{
                                            fontSize: "14px",
                                            fontFamily: "monospace",
                                        }}
                                    >
                                        {accountIdDisplay || "N/A"}
                                    </span>
                                    {accountIdDisplay && (
                                        <>
                                            <EditIcon
                                                onClick={handleEditAccountId}
                                                sx={{
                                                    fontSize: "1rem",
                                                    cursor: "pointer",
                                                    color: "#666",
                                                }}
                                            />
                                            <ContentCopy
                                                onClick={() => copyToClipboard(accountIdDisplay)}
                                                sx={{
                                                    fontSize: "1rem",
                                                    cursor: "pointer",
                                                    color: "#666",
                                                }}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "auto minmax(200px, 1fr)",
                                alignItems: "center",
                                width: "100%",
                                gap: "20px",
                            }}
                        >
                            {isKonnektDevice && (
                                <p style={{ margin: 0 }}>
                                    Videophone Profile Name :{" "}
                                    {isEditingKonnektId ? (
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newKonnektId}
                                                onChange={(e) => setNewKonnektId(e.target.value)}
                                                style={{
                                                    border: "none",
                                                    borderBottom: "1px solid #ddd",
                                                    outline: "none",
                                                    maxWidth: "250px",
                                                }}
                                            />
                                            <div style={{ display: "flex", gap: "8px" }}>
                                                <div
                                                    onClick={handleKonnektIdSubmitClick}
                                                    style={{ cursor: "pointer", color: "#4CAF50" }}
                                                >
                                                    <CheckIcon sx={{ fontSize: "1.2rem" }} />
                                                </div>
                                                <div
                                                    onClick={handleCancelEditKonnektId}
                                                    style={{ cursor: "pointer", color: "#FF5252" }}
                                                >
                                                    <XIcon sx={{ fontSize: "1.2rem" }} />
                                                </div>
                                            </div>
                                        </span>
                                    ) : (
                                        <>
                                            {device.konnektDeviceId || "N/A"}&nbsp;&nbsp;&nbsp;
                                            <EditIcon
                                                onClick={handleEditKonnektId}
                                                sx={{
                                                    color: "black",
                                                    marginRight: "10px",
                                                    cursor: "pointer",
                                                }}
                                            />
                                            &nbsp;&nbsp;&nbsp;
                                            {device.konnektDeviceId && (
                                                <ContentCopy
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(
                                                            device.konnektDeviceId
                                                        );
                                                        toast.success("Copied");
                                                    }}
                                                    sx={{
                                                        color: "black",
                                                        marginRight: "10px",
                                                        cursor: "pointer",
                                                    }}
                                                />
                                            )}
                                            <br />
                                            Care Window Device Id : {device.id}
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        marginTop: 10,
                        marginLeft: 40,
                        marginRight: 40,
                        marginBottom: 30,
                        padding: 15,
                        borderRadius: 6,
                        border: "1px solid #eee",
                        backgroundColor: "#fafafa",
                        fontSize: 14,
                    }}
                >
                    <h5 style={{ marginTop: 0, marginBottom: 10 }}>Device Info
                        &nbsp;&nbsp;
                        <EditIcon onClick={() => { openEditDevice(device); }} sx={{ cursor: "pointer", }} />
                    </h5>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: "8px 30px",
                        }}
                    >
                        <div>
                            <InfoRow label="Device ID:" value={device.id} />
                            <InfoRow label="Account ID:" value={accountIdDisplay} />
                            <InfoRow label="Org:" value={orgDisplay} />
                        </div>
                        <div>
                            <InfoRow label="Recovery email:" value={device.recoveryEmail} />
                            <InfoRow label="Serial No:" value={serialDisplay} />
                            <InfoRow label="Device Type:" value={deviceTypeDisplay} />
                            {isCareWindowDevice && (
                                <InfoRow label="Phone number:" value={sipPhoneDisplay} />
                            )}
                            <InfoRow label="Linked Devices:" value={linkedDevicesDisplay} />
                        </div>
                        <div>
                            <InfoRow label="App Version:" value={appVersionDisplay} />
                            <InfoRow
                                label="Preferred Language:"
                                value={preferredLangDisplay}
                            />
                            <InfoRow label="Created:" value={createdDisplay} />
                            <InfoRow label="Updated:" value={updatedDisplay} />
                        </div>
                        <div>
                            <InfoRow label="Time Zone:" value={timeZoneDisplay} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="full inner_elements">
                <div className="row">
                    <div className="col-md-12">
                        <div className="tab_style1">
                            <div
                                className="tabbar padding_infor_info"
                                style={{ display: "block", paddingTop: "25px" }}
                            >
                                <nav>
                                    <div className="nav nav-tabs" id="nav-tab" role="tablist">
                                        <div
                                            className={`nav-item nav-link ${selectedTab === "Contacts" ? "active" : ""
                                                }`}
                                            onClick={() => setSelectedTab("Contacts")}
                                        >
                                            Contacts
                                        </div>
                                        {isCareWindowDevice && (
                                            <div
                                                className={`nav-item nav-link ${selectedTab === "SIP Info" ? "active" : ""
                                                    }`}
                                                onClick={() => setSelectedTab("SIP Info")}
                                            >
                                                SIP Info
                                            </div>
                                        )}
                                        <div
                                            className={`nav-item nav-link ${selectedTab === "Settings" ? "active" : ""
                                                }`}
                                            onClick={() => setSelectedTab("Settings")}
                                        >
                                            Device Settings
                                        </div>

                                        <div
                                            className={`nav-item nav-link ${selectedTab === "Features" ? "active" : ""
                                                }`}
                                            onClick={() => setSelectedTab("Features")}
                                        >
                                            Features
                                        </div>
                                        <div
                                            className={`nav-item nav-link ${selectedTab === "Call Logs" ? "active" : ""
                                                }`}
                                            onClick={() => setSelectedTab("Call Logs")}
                                        >
                                            Call Logs
                                        </div>
                                    </div>
                                </nav>

                                <div className="tab-content" id="nav-tabContent">
                                    <div
                                        className={`tab-pane fade ${selectedTab === "Contacts" ? "show active" : ""
                                            }`}
                                        id="nav-contacts"
                                        role="tabpanel"
                                    >
                                        <ContactsTab
                                            user={device} openEditDevice={openEditDevice}
                                            fetchLinkedDevices={() => loadLinkedDevices(deviceId)}
                                            linkedDevices={linkedDevices}
                                            setLinkedDevices={setLinkedDevices}
                                            selectedOrg={null}
                                        />
                                    </div>

                                    {isCareWindowDevice && (
                                        <div
                                            className={`tab-pane fade ${selectedTab === "SIP Info" ? "show active" : ""
                                                }`}
                                            id="nav-sip-info"
                                            role="tabpanel"
                                        >
                                            <SipInfoTab device={device} />
                                        </div>
                                    )}

                                    <div
                                        className={`tab-pane fade ${selectedTab === "Settings" ? "show active" : ""
                                            }`}
                                        id="nav-settings"
                                        role="tabpanel"
                                    >
                                        <DeviceSettingsTab
                                            device={device}
                                            onDeviceUpdated={() =>
                                                refreshDevice({ reloadLinkedDevices: false })
                                            }
                                            selectedOrg={null}
                                            linkedDevices={linkedDevices}
                                        />
                                    </div>

                                    <div
                                        className={`tab-pane fade ${selectedTab === "Features" ? "show active" : ""
                                            }`}
                                        id="nav-features"
                                        role="tabpanel"
                                    >
                                        <DeviceFeaturesTab device={device} />
                                    </div>

                                    <div
                                        className={`tab-pane fade ${selectedTab === "Call Logs" ? "show active" : ""
                                            }`}
                                        id="nav-call-logs"
                                        role="tabpanel"
                                    >
                                        <CallLogsTab device={device} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showUpdateAvatarModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Update Profile Picture</h1>
                                <button className="close-button" onClick={handleCancelAvatar}>
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to update the profile picture?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={handleCancelAvatar}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleConfirmAvatarUpdate}
                                >
                                    Update
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showRenameModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Rename Device</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowRenameModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to rename this device from "
                                    {device.name}" to "{newName}"?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowRenameModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleRenameConfirm}
                                >
                                    Rename
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showRenameKonnektIdModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Rename Konnekt Id</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowRenameKonnektIdModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to rename this device konnekt Id from "
                                    {device.konnektDeviceId}" to "{newKonnektId}"?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowRenameKonnektIdModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleRenameKonnektIdConfirm}
                                >
                                    Rename
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showUpdateAccountIdModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "500px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Update Account ID</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowUpdateAccountIdModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p
                                    style={{
                                        marginBottom: "10px",
                                        fontWeight: "bold",
                                        color: "#d9534f",
                                    }}
                                >
                                    ⚠️ Warning: This is a critical operation!
                                </p>
                                <p>Are you sure you want to change the Account ID from:</p>
                                <p
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        marginLeft: "10px",
                                    }}
                                >
                                    <strong>Old:</strong> {device.accountId || "N/A"}
                                </p>
                                <p
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        marginLeft: "10px",
                                    }}
                                >
                                    <strong>New:</strong> {newAccountId}
                                </p>
                                <p
                                    style={{
                                        marginTop: "10px",
                                        fontSize: "13px",
                                        color: "#666",
                                    }}
                                >
                                    This will move the device to a different account and notify
                                    all linked devices.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowUpdateAccountIdModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-danger"
                                    onClick={handleUpdateAccountIdConfirm}
                                >
                                    Update Account ID
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {iseditDeviceInfo && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "500px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title" style={{ fontWeight: 700 }}>Edit Device Info</h1>
                                <button className="close-button" onClick={() => setiseditDeviceInfo(false)} > &times; </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group" style={{ marginBottom: "10px" }}>
                                    <label style={{ color: "black" }} > Recovery email </label>
                                    <input className="form-control" type="email" value={editrecoveryEmail} onChange={(e) => { seteditrecoveryEmail(e.target.value); }}></input>
                                </div>
                                <div className="form-group" style={{ marginBottom: "10px" }}>
                                    <label style={{ color: "black" }} ><input type="checkbox" checked={editisManager} onChange={(e) => { seteditisManager(e.target.checked); }} />&nbsp;is Manager </label>
                                </div>
                                {/* {isKonnektDevice &&
                                    <>
                                        <div className="form-group" style={{ marginBottom: "10px" }}>
                                            <label style={{ color: "black" }} > Short name </label>
                                            <input type="text" className="form-control" value={editShortName} onChange={(e) => { seteditShortName(e.target.value); }} />
                                        </div>
                                        <div className="form-group" style={{ marginBottom: "10px" }}>
                                            <label style={{ color: "black" }} >Konnekt skype id </label>
                                            <input type="text" className="form-control" value={editKonnektskypeid} onChange={(e) => { seteditKonnektskypeid(e.target.value); }} />
                                        </div>
                                    </>
                                } */}
                                <div className="form-group" style={{ marginBottom: "10px" }}>
                                    <label style={{ color: "black" }} >Konnekt skype id </label>
                                    <input type="text" className="form-control" value={editKonnektskypeid} onChange={(e) => { seteditKonnektskypeid(e.target.value); }} />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: "20px" }}>
                                <label style={{ color: "black" }} >Preffered language</label>
                                <select value={editPrefferedlanguage} onChange={(e) => seteditPrefferedlanguage(e.target.value)} className="form-control">
                                    <option value="af">Afrikaans</option>
                                    <option value="ar">Arabic</option>
                                    <option value="hy">Armenian</option>
                                    <option value="az">Azerbaijani</option>
                                    <option value="be">Belarusian</option>
                                    <option value="bs">Bosnian</option>
                                    <option value="bg">Bulgarian</option>
                                    <option value="ca">Catalan</option>
                                    <option value="zh">Chinese</option>
                                    <option value="hr">Croatian</option>
                                    <option value="cs">Czech</option>
                                    <option value="da">Danish</option>
                                    <option value="nl">Dutch</option>
                                    <option value="en">English</option>
                                    <option value="et">Estonian</option>
                                    <option value="fi">Finnish</option>
                                    <option value="fr">French</option>
                                    <option value="gl">Galician</option>
                                    <option value="de">German</option>
                                    <option value="el">Greek</option>
                                    <option value="he">Hebrew</option>
                                    <option value="hi">Hindi</option>
                                    <option value="hu">Hungarian</option>
                                    <option value="is">Icelandic</option>
                                    <option value="id">Indonesian</option>
                                    <option value="it">Italian</option>
                                    <option value="ja">Japanese</option>
                                    <option value="kn">Kannada</option>
                                    <option value="kk">Kazakh</option>
                                    <option value="ko">Korean</option>
                                    <option value="lv">Latvian</option>
                                    <option value="lt">Lithuanian</option>
                                    <option value="mk">Macedonian</option>
                                    <option value="ms">Malay</option>
                                    <option value="mr">Marathi</option>
                                    <option value="mi">Maori</option>
                                    <option value="ne">Nepali</option>
                                    <option value="nb">Norwegian</option>
                                    <option value="fa">Persian</option>
                                    <option value="pl">Polish</option>
                                    <option value="pt">Portuguese</option>
                                    <option value="ro">Romanian</option>
                                    <option value="ru">Russian</option>
                                    <option value="sr">Serbian</option>
                                    <option value="sk">Slovak</option>
                                    <option value="sl">Slovenian</option>
                                    <option value="es">Spanish</option>
                                    <option value="sw">Swahili</option>
                                    <option value="sv">Swedish</option>
                                    <option value="tl">Tagalog</option>
                                    <option value="ta">Tamil</option>
                                    <option value="th">Thai</option>
                                    <option value="tr">Turkish</option>
                                    <option value="uk">Ukrainian</option>
                                    <option value="ur">Urdu</option>
                                    <option value="vi">Vietnamese</option>
                                    <option value="cy">Welsh</option>
                                    <option value="yue">Cantonese</option>
                                    <option value="minnan">MinNanese Chines dialect</option>
                                </select>
                            </div>
                            <button className="btn btn-dark " onClick={handleUpdateDevice} style={{ marginBottom: "30px", width: "150px" }} > Update </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DeviceDetail;
